import React from "react";
import { Box, styled } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { SENTIMENT_COLORS, SENTIMENT_LABELS } from "./constants";
import { normalizeSentiment } from "./normalizeSentiment";
import { SentimentGlyph } from "./SentimentGlyph";

interface SentimentBadgeProps {
  /** Any stored spelling — normalized here. Renders nothing when unrated. */
  sentiment: string | null | undefined;
  /**
   * `tint` sits the glyph on its own pale background, for a badge over a light
   * card. `white` is for a badge over artwork, where the rating's tint would
   * disappear — it takes a shadow to lift off the image instead.
   */
  variant?: "tint" | "white";
  size?: number;
  sx?: SxProps<Theme>;
}

const Badge = styled(Box, {
  label: "SentimentBadge",
})(() => ({
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

/**
 * The rating stamped on the corner of a hat or pathway card. Callers position
 * it — a card that overlays it on artwork needs its own absolute wrapper.
 */
export const SentimentBadge = ({
  sentiment,
  variant = "tint",
  size = 40,
  sx,
}: SentimentBadgeProps) => {
  const key = normalizeSentiment(sentiment);
  if (!key) return null;

  return (
    <Badge
      sx={{
        width: size,
        height: size,
        ...(variant === "white"
          ? {
              backgroundColor: "#fff",
              boxShadow: "0 2px 8px 0 rgba(0,0,0,0.12)",
            }
          : { backgroundColor: SENTIMENT_COLORS[key].tint }),
        ...sx,
      }}
    >
      <SentimentGlyph
        sentiment={key}
        size={size * 0.55}
        title={SENTIMENT_LABELS[key]}
      />
    </Badge>
  );
};
